// Clocks (one place or several), a countdown, a focus timer, a calendar and
// the weather.

import { lazy, memo, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { eventDay, upcomingEvents } from "../lib/eventDay";
import { CalendarPlus, TriangleAlert, Cloud, CloudDrizzle, CloudFog, CloudLightning, CloudRain, CloudSnow, CloudSun, Moon, Pause, Play, RotateCcw, SkipForward, Sun, Sunrise, Search } from "lucide-react";
import { slugifyKey } from "../../shared/ids";
import type { StreamItem } from "../../shared/types";
import { api } from "../lib/api";
import { cx } from "../lib/util";
import { updateItem, updateProps } from "../state/actions";
import { createPull } from "../state/sources";
import { set, toast, useStore } from "../state/store";
import { kindOf } from "../../shared/boardKinds";
import { useShallow } from "zustand/react/shallow";
import { useSize } from "../charts/charts";
import type { WP } from "./data";
import { quarter } from "./layout";
import { calendarFit, cityOf, clockFace, clockZones, countFit, digitalFit, partsIn, placeAt, timerFit, utcOffset, weatherFit, worldFit, type Place } from "./plans";
import { useTick } from "./tick";
import { openConnect, Setup } from "./setup";
import { readStoredJson, writeStoredJson } from "../lib/stored";

export { cityOf, clockZones, partsIn };

function useNow(ms: number): Date {
  const [d, setD] = useState(() => new Date());
  useEffect(() => {
    // align to the boundary so seconds tick together across clocks
    let t: ReturnType<typeof setTimeout>;
    const tick = () => {
      setD(new Date());
      t = setTimeout(tick, ms - (Date.now() % ms));
    };
    t = setTimeout(tick, ms - (Date.now() % ms));
    return () => clearTimeout(t);
  }, [ms]);
  return d;
}

// ---------------------------------------------------------------------------
// Clock: the time here or in one place, as hands or as digits, or the time in
// several places at once. The world clock was its own widget until 11 Oct
// 2026; a saved one draws here, with its places.

/** The hours and minutes a clock shows, and its am or pm (none on a 24-hour clock). */
function hhmm(h: number, m: number, h24: boolean): { hh: string; mm: string; ap: string | null } {
  return { hh: h24 ? String(h).padStart(2, "0") : String(h % 12 || 12), mm: String(m).padStart(2, "0"), ap: h24 ? null : h < 12 ? "am" : "pm" };
}

/** Hands on a face with twelve marks, the quarters bolder. The face draws itself round at any size of tile. */
function Face({ h, m, s, seconds, disc }: { h: number; m: number; s: number; seconds: boolean; disc?: "light" | "dark" }) {
  const min = m + s / 60;
  const hr = (h % 12) + min / 60;
  return (
    <svg className={cx("ck-face", disc && `is-${disc}`)} viewBox="0 0 100 100" aria-hidden>
      {disc && <circle className="ck-disc" cx="50" cy="50" r="48.5" />}
      {Array.from({ length: 12 }, (_, i) => (
        <line key={i} className={i % 3 === 0 ? "ck-tk is-q" : "ck-tk"} x1="50" y1="7" x2="50" y2={i % 3 === 0 ? 14 : 11} transform={`rotate(${i * 30} 50 50)`} />
      ))}
      <line className="ck-hh" x1="50" y1="50" x2="50" y2="28" transform={`rotate(${hr * 30} 50 50)`} />
      <line className="ck-mh" x1="50" y1="50" x2="50" y2="17" transform={`rotate(${min * 6} 50 50)`} />
      {seconds && <line className="ck-sh" x1="50" y1="56" x2="50" y2="14" transform={`rotate(${s * 6} 50 50)`} />}
      <circle className="ck-ctr" cx="50" cy="50" r="2.6" />
    </svg>
  );
}

/** Where the place is in its day: night at both ends, light in the middle, and a mark at now. */
function DayBar({ place }: { place: Place }) {
  return (
    <span className={cx("ck-daybar", place.light && "is-light")} aria-hidden>
      <i style={{ left: `${place.through * 100}%` }} />
    </span>
  );
}

export function Clock({ item }: WP) {
  const zones = clockZones(item.props);
  return zones.length > 1 ? <WorldClock item={item} zones={zones} /> : <OneClock item={item} tz={zones[0]} />;
}

function OneClock({ item, tz }: { item: WP["item"]; tz: string | undefined }) {
  const analog = clockFace(item.type, item.props) === "analog";
  const seconds = item.props.seconds !== false;
  const h24 = item.props.h24 === true;
  const d = useNow(analog && seconds ? 1000 : 10_000);
  const [ref, size] = useSize<HTMLDivElement>();
  const p = partsIn(d, tz);
  if (analog)
    return (
      <div className="ck-analog" ref={ref}>
        <Face h={p.h} m={p.m} s={p.s} seconds={seconds} />
        {tz && <span className="ck-cap">{cityOf(tz)}</span>}
      </div>
    );
  const t = hhmm(p.h, p.m, h24);
  const fit = digitalFit(size);
  return (
    <div className={cx("ck-digital", `is-${fit.id}`)} ref={ref} style={fit.px ? { fontSize: quarter(fit.px) } : undefined}>
      <div className="ck-time">
        {t.hh}
        <span className="ck-colon">:</span>
        {t.mm}
        {t.ap && <span className="ck-ap">{t.ap}</span>}
      </div>
      {fit.id === "full" && (
        <div className="ck-date">
          {p.weekday}, {p.month} {p.day}
          {tz ? ` · ${cityOf(tz)}` : ""}
        </div>
      )}
    </div>
  );
}

function WorldClock({ item, zones }: { item: WP["item"]; zones: string[] }) {
  const analog = clockFace(item.type, item.props) === "analog";
  const seconds = analog && item.props.seconds !== false;
  const h24 = item.props.h24 === true;
  const d = useNow(seconds ? 1000 : 10_000);
  const [ref, size] = useSize<HTMLDivElement>();
  const fit = worldFit(size, zones.length, analog);
  const { bare } = fit;
  const places = zones.slice(0, fit.shown).map((z) => placeAt(d, z));
  const hidden = zones.length - fit.shown;
  return (
    <div className={cx("ck-world", `is-${fit.id}`, analog ? "is-analog" : "is-digital", bare && "is-bare")} ref={ref} style={fit.px ? { fontSize: quarter(fit.px), ["--n" as string]: fit.shown } : undefined}>
      {places.map((pl, i) => {
        const t = hhmm(pl.h, pl.m, h24);
        // in rows there is room to say tomorrow; side by side the day's short name says it
        const short = pl.day === "Today" ? "" : ` · ${new Date(d.getTime() + (utcOffset(d, pl.tz) - utcOffset(d)) * 60_000).toLocaleDateString("en-US", { weekday: "short" })}`;
        const when = pl.diff === "Here" ? "Here" : pl.day === "Today" ? pl.diff : fit.id === "rows" ? `${pl.day}, ${pl.diff}` : `${pl.diff}${short}`;
        return (
          <div key={pl.tz} className={cx("ck-place", pl.light ? "is-light" : "is-dark")} title={`${pl.city}: ${t.hh}:${t.mm}${t.ap ?? ""}, ${pl.day.toLowerCase()}${pl.diff === "Here" ? "" : `, ${pl.diff} from here`}`}>
            {analog && <Face h={pl.h} m={pl.m} s={pl.s} seconds={seconds} disc={pl.light ? "light" : "dark"} />}
            {!analog && fit.id === "rows" && (pl.light ? <Sun className="ck-sun" /> : <Moon className="ck-moon" />)}
            <span className="ck-city">
              <b>{pl.city}</b>
              {!bare && <small>{when}</small>}
              {hidden > 0 && i === places.length - 1 && (
                <em className="ck-more" title={`${hidden} more place${hidden === 1 ? "" : "s"} show in a bigger tile`}>
                  +{hidden}
                </em>
              )}
            </span>
            {(!analog || fit.id === "rows") && (
              <span className="ck-t">
                {t.hh}:{t.mm}
                {t.ap && <span className="ck-ap">{t.ap}</span>}
              </span>
            )}
            {!analog && !bare && fit.id !== "rows" && <DayBar place={pl} />}
          </div>
        );
      })}
    </div>
  );
}

/** A countdown: a card for each of days, hours, minutes and seconds, like the landing page's, in a row in a wide
 * tile and a grid in a square one, or the biggest unit alone in a small one. Counts up, "since", once the day passes. */
export function Countdown({ item, readOnly }: WP) {
  const d = useNow(1000);
  const [ref, size] = useSize<HTMLDivElement>();
  const to = typeof item.props.to === "string" ? item.props.to : "";
  const target = to ? new Date(to).getTime() : NaN;
  const label = String(item.props.label ?? "").trim();
  if (!to || !Number.isFinite(target))
    return (
      <div className="cd is-empty" ref={ref}>
        <span className="cd-label">{label || "Countdown"}</span>
        {readOnly ? (
          <span className="faint">No date set</span>
        ) : (
          <label className="cd-pick" data-interactive>
            <span>Pick the day</span>
            <input type="date" onChange={(e) => e.target.value && updateProps(item.id, { to: e.target.value + "T09:00" }, "Set countdown")} />
          </label>
        )}
      </div>
    );
  const diff = target - d.getTime();
  const past = diff < 0;
  const a = Math.abs(diff);
  const days = Math.floor(a / 86_400_000);
  const parts = [
    ...(days > 0 ? [{ n: days, u: days === 1 ? "day" : "days" }] : []),
    { n: Math.floor((a % 86_400_000) / 3_600_000), u: "hours" },
    { n: Math.floor((a % 3_600_000) / 60_000), u: "min" },
    { n: Math.floor((a % 60_000) / 1000), u: "sec" },
  ];
  const fit = countFit(size, parts.length, String(days).length);
  const when = new Date(target).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  // what it counts to, unless the tile's title says it already
  const named = label && label.toLowerCase() !== (item.title || "").trim().toLowerCase() ? label : "";
  const first = parts.find((p) => p.n > 0) ?? parts[parts.length - 1];
  return (
    <div className={cx("cd", `is-${fit.id}`, past && "is-past")} ref={ref} style={fit.px ? { fontSize: quarter(fit.px) } : undefined} title={`${label || "Countdown"}: ${new Date(target).toLocaleString("en-US", { dateStyle: "full", timeStyle: "short" })}`}>
      <div className="cd-head">
        {named && <span className="cd-label">{named}</span>}
        <span className="cd-when">{past ? `Since ${when}` : when}</span>
      </div>
      {fit.id === "alone" ? (
        <div className="cd-alone">
          <b>{first.n}</b>
          <span>
            {first.n === 1 ? first.u.replace(/s$/, "") : first.u} {past ? "since" : "to go"}
          </span>
        </div>
      ) : (
        <div className="cd-cards" style={{ ["--cols" as string]: fit.id === "row" ? parts.length : parts.length === 4 ? 2 : parts.length }}>
          {parts.map((p, i) => (
            <div key={p.u} className="cd-card">
              <b key={p.n}>{i === 0 && p.u.startsWith("day") ? p.n : String(p.n).padStart(2, "0")}</b>
              <span>{p.u}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Focus timer: keeps running across reloads, chimes when a round ends

interface TimerState {
  mode: "work" | "rest";
  endsAt: number | null;
  left: number; // ms remaining when paused
  rounds: number;
}

function chime() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const notes = [880, 1318.5, 1760];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      g.gain.setValueAtTime(0, ctx.currentTime + i * 0.16);
      g.gain.linearRampToValueAtTime(0.16, ctx.currentTime + i * 0.16 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.16 + 1.2);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.16);
      o.stop(ctx.currentTime + i * 0.16 + 1.3);
    });
  } catch {
    /* no audio */
  }
}

export function Timer({ item, readOnly }: WP) {
  const work = Math.max(1, Number(item.props.work ?? 25)) * 60_000;
  const rest = Math.max(1, Number(item.props.rest ?? 5)) * 60_000;
  const storeKey = "cb:timer:" + item.id;
  const [st, setSt] = useState<TimerState>(() => readStoredJson<TimerState | null>(storeKey, null) ?? { mode: "work", endsAt: null, left: work, rounds: 0 });
  const [ref, size] = useSize<HTMLDivElement>();
  const now = useNow(st.endsAt ? 250 : 5000).getTime();
  useEffect(() => {
    writeStoredJson(storeKey, st);
  }, [st, storeKey]);
  const total = st.mode === "work" ? work : rest;
  const left = Math.min(total, st.endsAt ? Math.max(0, st.endsAt - now) : st.left);
  useEffect(() => {
    if (st.endsAt && left <= 0) {
      chime();
      const nextMode = st.mode === "work" ? "rest" : "work";
      toast({ level: "success", title: st.mode === "work" ? "Focus round done. Take five." : "Break over. Back to it." });
      setSt({ mode: nextMode, endsAt: null, left: nextMode === "work" ? work : rest, rounds: st.mode === "work" ? st.rounds + 1 : st.rounds });
    }
  }, [left, st, work, rest]);
  const running = !!st.endsAt;
  const fresh = !running && left >= total;
  const toggle = () => setSt(running ? { ...st, endsAt: null, left } : { ...st, endsAt: Date.now() + left });
  const reset = () => setSt({ ...st, endsAt: null, left: total });
  const skip = () => setSt({ mode: st.mode === "work" ? "rest" : "work", endsAt: null, left: st.mode === "work" ? rest : work, rounds: st.rounds });
  const fit = timerFit(size);
  const mm = Math.floor(left / 60_000);
  const ss = Math.floor((left % 60_000) / 1000);
  const time = (
    <b className={cx("tm-time", mm >= 100 && "is-long")}>
      {mm}:{String(ss).padStart(2, "0")}
    </b>
  );
  // the focus rounds done in this set of four, as dots after the mode
  const done = st.rounds % 4 || (st.rounds ? 4 : 0);
  const rounds = st.rounds > 0 && (
    <span className="tm-rounds" title={`${st.rounds} focus round${st.rounds === 1 ? "" : "s"} done`}>
      {[0, 1, 2, 3].map((i) => (
        <i key={i} className={cx(i < done && "is-on")} />
      ))}
    </span>
  );
  const word = st.mode === "work" ? "Focus" : "Break";
  // beside the ring there is room for the round's length; inside it the time says it, and the dots go under the
  // word, so nothing reaches the ring (David, 14 Oct: after a round the line ran out across it)
  const mode = (
    <span className="tm-mode">
      {running && <i className="tm-live" />}
      {fresh ? `${word} · ${Math.round((st.mode === "work" ? work : rest) / 60_000)} min` : word}
      {rounds}
    </span>
  );
  const modeInside = (
    <>
      <span className="tm-mode">
        {running && <i className="tm-live" />}
        {word}
      </span>
      {rounds}
    </>
  );
  const go = (
    <button className={cx("tm-go", fit.id === "ring" && "is-round")} onClick={toggle} aria-label={running ? "Pause" : fresh ? "Start" : "Resume"} data-interactive>
      {running ? <Pause /> : <Play />}
      {fit.id !== "ring" && <span>{running ? "Pause" : fresh ? "Start" : "Resume"}</span>}
    </button>
  );
  const controls = !readOnly && (
    <div className="tm-controls" data-interactive>
      {go}
      {fit.id !== "ring" && !fresh && (
        <button className="tm-icon" onClick={reset} aria-label="Start this round again" title="Start this round again">
          <RotateCcw />
        </button>
      )}
      {fit.id !== "ring" && (
        <button className="tm-icon" onClick={skip} aria-label={st.mode === "work" ? "Skip to the break" : "Skip the break"} title={st.mode === "work" ? "Skip to the break" : "Skip the break"}>
          <SkipForward />
        </button>
      )}
    </div>
  );
  const ring = (
    <div className={cx("tm-ring", fit.id === "dot" && !readOnly && "is-button")} onClick={fit.id === "dot" && !readOnly ? toggle : undefined} role={fit.id === "dot" && !readOnly ? "button" : undefined} aria-label={fit.id === "dot" && !readOnly ? (running ? "Pause" : "Start") : undefined} data-interactive={fit.id === "dot" && !readOnly ? "" : undefined}>
      <svg viewBox="0 0 100 100" aria-hidden>
        <circle className="tm-trk" cx="50" cy="50" r="44" />
        <circle className="tm-val" cx="50" cy="50" r="44" pathLength={100} strokeDasharray="100 100" strokeDashoffset={100 * (1 - left / total)} transform="rotate(-90 50 50)" />
      </svg>
      {fit.id !== "side" && (
        <div className="tm-in">
          {time}
          {fit.id === "stack" && modeInside}
        </div>
      )}
    </div>
  );
  return (
    <div className={cx("tm", `is-${fit.id}`, st.mode === "rest" && "is-rest", running && "is-running")} ref={ref} style={fit.px ? { fontSize: quarter(fit.px) } : undefined}>
      {ring}
      {fit.id === "side" ? (
        <div className="tm-side">
          {mode}
          {time}
          {controls}
        </div>
      ) : (
        fit.id !== "dot" && controls
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar

/** The day a week starts on where the user is: 0 for Sunday, 1 for Monday. */
function weekStart(): number {
  try {
    const loc = new Intl.Locale(navigator.language) as Intl.Locale & { weekInfo?: { firstDay: number }; getWeekInfo?: () => { firstDay: number } };
    const day = (loc.getWeekInfo?.() ?? loc.weekInfo)?.firstDay;
    return day === 7 || day === undefined ? 0 : day % 7;
  } catch {
    return 0;
  }
}

/** "Today", "Tomorrow" or "Wed 14", for an event's day. */
function dayName(day: Date, now: Date): string {
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const n = Math.round((new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime() - a) / 86_400_000);
  return n === 0 ? "Today" : n === 1 ? "Tomorrow" : `${day.toLocaleDateString("en-US", { weekday: "short" })} ${day.getDate()}`;
}

/**
 * The month, as big as the tile allows: whole weeks from where the user's
 * week starts, the days of the months either side faint, today picked out,
 * days with events marked, and their names inside the days when the days are
 * big enough. The next events sit beside the month or under it when there
 * is room. Any calendar's link connects it, or a Calendar board (Features 6): then it shows what that board shows.
 */
export function Calendar(p: WP) {
  const board = typeof p.item.props.board === "string" ? p.item.props.board : null;
  if (!board) return <CalendarView {...p} />;
  return (
    <Suspense fallback={<CalendarView {...p} items={[]} />}>
      <BoardCalendar {...p} board={board} />
    </Suspense>
  );
}

/** A Calendar board's events in the widget: loaded only where a widget shows one (./boardCalendar.tsx). */
const BoardCalendar = lazy(() => import("./boardCalendar"));

/** The calendar widget as drawn: its stream's events, or those handed to it (a Calendar board's), and what opening one does. */
export function CalendarView({ item, stream, readOnly, items, onOpen, onMonth }: WP & { items?: StreamItem[]; onOpen?: (e: StreamItem) => void; /** the month shown, its first day "YYYY-MM-01", for events read for it */ onMonth?: (day: string) => void }) {
  const d = useNow(60_000);
  const [offset, setOffset] = useState(0);
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [ref, size] = useSize<HTMLDivElement>();
  const first = useMemo(weekStart, []);
  const view = new Date(d.getFullYear(), d.getMonth() + offset, 1);
  const month = `${view.getFullYear()}-${String(view.getMonth() + 1).padStart(2, "0")}-01`;
  useEffect(() => onMonth?.(month), [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const events = items ?? ((stream?.items ?? []) as StreamItem[]);
  // the account's Calendar boards, to show one of them in place of a calendar link
  const calBoards = useStore(useShallow((s) => (readOnly || items ? [] : s.boards.filter((b) => kindOf(b) === "agenda" && !b.archived))));
  const byDay = useMemo(() => {
    const m = new Map<string, StreamItem[]>();
    for (const e of events) {
      if (!e.t) continue;
      const k = eventDay(e).toDateString();
      m.set(k, [...(m.get(k) ?? []), e]);
    }
    return m;
  }, [events]);
  const lead = (view.getDay() - first + 7) % 7;
  const daysIn = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const weeks = Math.ceil((lead + daysIn) / 7);
  const cells = Array.from({ length: weeks * 7 }, (_, i) => new Date(view.getFullYear(), view.getMonth(), 1 - lead + i));
  const dows = Array.from({ length: 7 }, (_, i) => new Date(2026, 0, 4 + first + i).toLocaleDateString("en-US", { weekday: "narrow" }));
  // the next events, in the order they happen here: the stream's order is UTC's
  const upcoming = upcomingEvents(events, d.getTime(), 6);
  // before the tile has been measured (its first paint, or drawn to a string), its own size stands in
  const fit = calendarFit(size.w ? size : { w: item.w - 28, h: item.h - 52 }, upcoming.length > 0);
  const today = d.toDateString();
  // tried before anything is added: a link that does not read says why, in the form, and nothing is made
  const connect = async () => {
    const u = url.trim().replace(/^webcal:\/\//i, "https://");
    if (!/^https?:\/\//.test(u)) {
      setProblem("Paste the whole link, starting with https:// or webcal://.");
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const t = await api<{ ok: boolean; error: string | null }>("/api/pulls/test", { body: { kind: "ical", config: { url: u } } });
      if (!t.ok) {
        // a page's own words, never its markup: "example.com answered 404."
        setProblem(t.error ? t.error.replace(/:\s*<[\s\S]*$/, ".") : "That link did not give a calendar.");
        return;
      }
      const key = slugifyKey(`cal-${item.id.slice(-6)}`);
      await createPull({ kind: "ical", name: "Calendar", stream: key, config: { url: u }, every: 1800 });
      updateItem(item.id, { stream: key } as never, "Connect calendar");
      setAdding(false);
      setUrl("");
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const when = (e: StreamItem) => (e.value === "all-day" ? "All day" : new Date(e.t!).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase());
  return (
    <div className={cx("cal", `is-${fit.id}`, fit.titles && "has-titles")} ref={ref} style={fit.px ? { fontSize: quarter(fit.px) } : undefined}>
      <div className="cal-month">
        <div className="cal-head">
          <b>{view.toLocaleDateString("en-US", { month: "long", year: view.getFullYear() !== d.getFullYear() ? "numeric" : undefined })}</b>
          {offset !== 0 && (
            <button className="cal-today" onClick={() => setOffset(0)} data-interactive>
              Today
            </button>
          )}
          <button className="cal-nav" onClick={() => setOffset(offset - 1)} aria-label="Previous month" data-interactive>
            ‹
          </button>
          <button className="cal-nav" onClick={() => setOffset(offset + 1)} aria-label="Next month" data-interactive>
            ›
          </button>
        </div>
        <div className="cal-grid" style={{ gridTemplateRows: `auto repeat(${weeks}, minmax(0, 1fr))` }}>
          {dows.map((x, i) => (
            <span key={i} className="cal-dow">
              {x}
            </span>
          ))}
          {cells.map((date) => {
            const key = date.toDateString();
            const ev = byDay.get(key);
            const other = date.getMonth() !== view.getMonth();
            const weekend = date.getDay() === 0 || date.getDay() === 6;
            return (
              <span key={key} className={cx("cal-day", other && "is-other", key === today && "is-today", ev && "has-events", weekend && "is-weekend")} title={ev?.map((e) => `${when(e)} ${e.label}`).join("\n")}>
                <b>{date.getDate()}</b>
                {ev && !fit.titles && (
                  <span className="cal-dots">
                    {[...new Set(ev.map((e) => e.color ?? ""))].slice(0, 3).map((c) => (
                      <i key={c} className="cal-dot" style={c ? ({ "--ev": c } as React.CSSProperties) : undefined} />
                    ))}
                  </span>
                )}
                {ev && fit.titles && (
                  <span className="cal-chips">
                    {ev.slice(0, 2).map((e, i) => (
                      <em key={i} className={e.color ? "has-ev" : undefined} style={e.color ? ({ "--ev": e.color } as React.CSSProperties) : undefined}>
                        {e.label}
                      </em>
                    ))}
                    {ev.length > 2 && <small>+{ev.length - 2}</small>}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </div>
      {fit.next && upcoming[0] && (
        <div className="cal-next" title={`${upcoming[0].label}, ${eventDay(upcoming[0]).toDateString()}`}>
          <span>Next</span>
          <b>{upcoming[0].label}</b>
          <span>{eventDay(upcoming[0]).getMonth() === d.getMonth() ? dayName(eventDay(upcoming[0]), d) : eventDay(upcoming[0]).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
        </div>
      )}
      {fit.id !== "month" && (
        <div className="cal-agenda" data-scroll>
          {upcoming.slice(0, fit.id === "side" ? 6 : 4).map((e, i) => (
            <div key={i} className={cx("cal-ev", onOpen && "is-link")} onClick={onOpen ? () => onOpen(e) : undefined} data-interactive={onOpen ? true : undefined}>
              <span className="cal-ev-day">{dayName(eventDay(e), d)}</span>
              <span className="cal-ev-title">{e.label}</span>
              <span className="cal-ev-time">{when(e)}</span>
            </div>
          ))}
        </div>
      )}
      {!readOnly && !item.stream && !items && !adding && (
        <div className="cal-connect" data-interactive>
          <button className="cal-add" onClick={() => setAdding(true)}>
            <CalendarPlus /> Show your events
          </button>
        </div>
      )}
      {!readOnly && item.stream && stream?.error && (
        <button className="cal-problem" onClick={() => set({ panel: "source", panelArg: { pullId: stream.pullId, stream: stream.key } })} title={stream.error} data-interactive>
          <TriangleAlert /> {events.length ? "Could not refresh your events" : "Could not read your calendar"}
        </button>
      )}
      {!readOnly && adding && !item.stream && (
        <div className="cal-setup">
          <Setup
            icon={<CalendarPlus />}
            title="Show your events"
            sub="Paste your calendar's iCal link. In Google Calendar it is in your calendar's settings, under Secret address in iCal format."
            problem={problem}
            note={
              <>
                {/* a Calendar board of the account's own, as it shows it, in one click (Features 6) */}
                {calBoards.map((b) => (
                  <span key={b.id}>
                    <button className="link-btn" onClick={() => (updateProps(item.id, { board: b.id }, "Show a Calendar board"), setAdding(false))}>
                      Show {b.name}
                    </button>
                    {" · "}
                  </span>
                ))}
                <button className="link-btn" onClick={() => openConnect(item, { preset: "holidays" })}>
                  Show public holidays instead
                </button>
                {" · "}
                <button className="link-btn" onClick={() => setAdding(false)}>
                  Cancel
                </button>
              </>
            }
          >
            <form
              className="setup-form"
              onSubmit={(e) => {
                e.preventDefault();
                void connect();
              }}
            >
              <input autoFocus placeholder="https://… .ics or webcal://…" aria-label="Your calendar's iCal link" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") setAdding(false);
              }} disabled={busy} />
              <button className="btn btn-sm btn-primary" type="submit" disabled={busy || !url.trim()}>
                {busy ? "Reading…" : "Show"}
              </button>
            </form>
          </Setup>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weather

const WMO: Record<number, [string, "sun" | "partly" | "cloud" | "fog" | "drizzle" | "rain" | "snow" | "storm"]> = {
  0: ["Clear", "sun"],
  1: ["Mostly clear", "sun"],
  2: ["Partly cloudy", "partly"],
  3: ["Overcast", "cloud"],
  45: ["Fog", "fog"],
  48: ["Freezing fog", "fog"],
  51: ["Light drizzle", "drizzle"],
  53: ["Drizzle", "drizzle"],
  55: ["Heavy drizzle", "drizzle"],
  56: ["Freezing drizzle", "drizzle"],
  57: ["Freezing drizzle", "drizzle"],
  61: ["Light rain", "rain"],
  63: ["Rain", "rain"],
  65: ["Heavy rain", "rain"],
  66: ["Freezing rain", "rain"],
  67: ["Freezing rain", "rain"],
  71: ["Light snow", "snow"],
  73: ["Snow", "snow"],
  75: ["Heavy snow", "snow"],
  77: ["Snow grains", "snow"],
  80: ["Showers", "rain"],
  81: ["Showers", "rain"],
  82: ["Heavy showers", "rain"],
  85: ["Snow showers", "snow"],
  86: ["Snow showers", "snow"],
  95: ["Thunderstorm", "storm"],
  96: ["Storm with hail", "storm"],
  99: ["Storm with hail", "storm"],
};

function WeatherIcon({ code, size = 20 }: { code: number; size?: number }) {
  const kind = WMO[code]?.[1] ?? "cloud";
  const p = { size, strokeWidth: 1.6 };
  switch (kind) {
    case "sun":
      return <Sun {...p} className="wx-sun" />;
    case "partly":
      return <CloudSun {...p} className="wx-partly" />;
    case "fog":
      return <CloudFog {...p} />;
    case "drizzle":
      return <CloudDrizzle {...p} className="wx-rain" />;
    case "rain":
      return <CloudRain {...p} className="wx-rain" />;
    case "snow":
      return <CloudSnow {...p} className="wx-snow" />;
    case "storm":
      return <CloudLightning {...p} className="wx-storm" />;
    default:
      return <Cloud {...p} />;
  }
}

interface WeatherJson {
  place: string | null;
  unit: string;
  wind: string;
  current: { temp: number; feels: number; code: number; wind: number; humidity: number; day: boolean };
  daily: { d: string; code: number; hi: number; lo: number; rain: number | null }[];
}

// the sky each kind of weather paints, by the WMO kind it falls in
const SKY: Record<string, string> = { sun: "clear", partly: "partly", cloud: "cloud", fog: "fog", drizzle: "drizzle", rain: "rain", snow: "snow", storm: "storm" };

/** A number between 0 and 1 that is always the same for the same i: where a drop, a flake or a star goes. */
const spot = (i: number, k: number) => {
  const x = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

/** The sky behind the weather: drawn, never fetched, so it works in a sealed board and costs nothing. */
const Sky = memo(function Sky({ sky, day }: { sky: string; day: boolean }) {
  const falling = sky === "rain" || sky === "drizzle" || sky === "storm" ? (sky === "drizzle" ? 14 : 26) : sky === "snow" ? 30 : 0;
  const stars = !day && (sky === "clear" || sky === "partly") ? 26 : 0;
  const clouds = sky === "partly" ? 2 : sky === "clear" ? 0 : 3;
  return (
    <div className="wx-sky" aria-hidden>
      {stars > 0 && (
        <div className="wx-stars">
          {Array.from({ length: stars }, (_, i) => (
            <i key={i} style={{ left: `${spot(i, 1) * 100}%`, top: `${spot(i, 2) * 62}%`, animationDelay: `${-spot(i, 3) * 4}s`, opacity: 0.35 + spot(i, 4) * 0.6 }} />
          ))}
        </div>
      )}
      {(sky === "clear" || sky === "partly") && <i className={cx("wx-orb", day ? "is-sun" : "is-moon")} />}
      {Array.from({ length: clouds }, (_, i) => (
        <i key={i} className={`wx-cloud c${i + 1}`} />
      ))}
      {falling > 0 && (
        <div className={cx("wx-fall", sky === "snow" ? "is-snow" : "is-rain")}>
          {Array.from({ length: falling }, (_, i) => (
            <i key={i} style={{ left: `${spot(i, 5) * 104 - 2}%`, animationDelay: `${-spot(i, 6) * 3}s`, animationDuration: `${sky === "snow" ? 5 + spot(i, 7) * 4 : 0.7 + spot(i, 7) * 0.5}s`, opacity: 0.4 + spot(i, 8) * 0.5 }} />
          ))}
        </div>
      )}
      {sky === "storm" && <i className="wx-flash" />}
    </div>
  );
});

/** The weather: a sky that matches it, now in big type over it, and the days to come along the foot, down the side or as rows, whichever fits the tile. */
export function Weather({ item, stream, readOnly }: WP) {
  const [ref, size] = useSize<HTMLDivElement>();
  const data = stream?.json as WeatherJson | undefined;
  if (!data?.current) return <PlaceSetup item={item} readOnly={readOnly} error={stream?.error ?? null} preset="weather" />;
  const c = data.current;
  const kind = WMO[c.code]?.[1] ?? "cloud";
  const sky = SKY[kind] ?? "cloud";
  const today = data.daily[0];
  const days = data.daily.slice(1, 6);
  const fit = weatherFit(size, days.length);
  // a pale sky (snow or fog by day) takes dark words
  const pale = c.day && (sky === "snow" || sky === "fog");
  return (
    <div className={cx("wx", `is-${sky}`, c.day ? "is-day" : "is-night", pale && "is-pale", `is-${fit.id}`)} ref={ref} style={fit.px ? { fontSize: quarter(fit.px) } : undefined} title={`${data.place ?? item.title}: ${Math.round(c.temp)}${data.unit}, ${WMO[c.code]?.[0] ?? ""}. Wind ${Math.round(c.wind)} ${data.wind}, humidity ${Math.round(c.humidity)}%.`}>
      <Sky sky={sky} day={c.day} />
      <div className="wx-now">
        <b className="wx-t">
          {Math.round(c.temp)}
          <sup>°</sup>
        </b>
        <span className="wx-c">{WMO[c.code]?.[0] ?? "—"}</span>
        <span className="wx-hl">
          {today ? `H ${Math.round(today.hi)}°  L ${Math.round(today.lo)}°` : ""}
          {today ? " · " : ""}Feels {Math.round(c.feels)}°
        </span>
      </div>
      {fit.id !== "now" && (
        <div className="wx-days">
          {days.slice(0, fit.days).map((x) => (
            <div key={x.d} className="wx-day">
              <span className="wx-dn">{new Date(x.d + "T12:00").toLocaleDateString("en-US", { weekday: "short" })}</span>
              <WeatherIcon code={x.code} size={16} />
              {fit.id === "list" && x.rain !== null && x.rain >= 20 && <span className="wx-rainp">{Math.round(x.rain)}%</span>}
              <span className="wx-hilo">
                {Math.round(x.hi)}° <i>{Math.round(x.lo)}°</i>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type PlaceHit = { name: string; region: string; lat: number; lon: number; tz?: string };

/** Where a widget that reads a place (the weather, the sun and moon) is for: search, pick, and its source is made. */
function PlaceSetup({ item, readOnly, error, preset }: { item: WP["item"]; readOnly: boolean; error: string | null; preset: "weather" | "sun" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<PlaceHit[]>([]);
  const [busy, setBusy] = useState(false);
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (t.current) clearTimeout(t.current);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    t.current = setTimeout(async () => {
      try {
        const r = await api<{ results: PlaceHit[] }>(`/api/geocode?q=${encodeURIComponent(q.trim())}`, { token: null });
        setResults(r.results.slice(0, 4));
      } catch {
        setResults([]);
      }
    }, 250);
  }, [q]);
  const pick = async (r: PlaceHit) => {
    setBusy(true);
    const key = slugifyKey(`${preset}-${r.name}`);
    try {
      const params = { place: r.name, lat: r.lat, lon: r.lon, ...(r.tz ? { tz: r.tz } : {}) };
      await createPull(preset === "sun" ? { kind: "preset", name: "Sun and moon", stream: key, config: { preset: "sun", params }, every: 3600 } : { kind: "preset", name: "Open-Meteo", stream: key, config: { preset: "weather", params }, every: 1800 });
      updateItem(item.id, { stream: key, title: r.name } as never, preset === "sun" ? "Set the sun and moon's place" : "Set weather place");
    } catch (e) {
      toast({ level: "error", title: preset === "sun" ? "Could not work out the sun and moon" : "Could not get the weather", body: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };
  if (readOnly) return <div className="empty"><span className="empty-title">No place set</span></div>;
  // one form throughout, so the box keeps the keys: what the widget shows until a search starts, then the places found
  const searching = q.trim().length >= 2;
  return (
    <div className={cx("setup", "place-setup", searching && "is-searching")} data-interactive>
      <span className="setup-ico">{preset === "sun" ? <Sunrise /> : <CloudSun />}</span>
      <b className="setup-title">{preset === "sun" ? "Sunrise, sunset and the moon" : "Weather for a place"}</b>
      {error ? (
        <span className="setup-sub is-bad">{error}</span>
      ) : (
        <span className="setup-sub">{preset === "sun" ? "Search a city to see its daylight today and the moon tonight." : "Search a city to see its weather now and for the next five days."}</span>
      )}
      <div className="wx-search">
        <Search size={13} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a city" aria-label="Search a city" onKeyDown={(e) => e.stopPropagation()} disabled={busy} />
      </div>
      {searching && (
        <div className="wx-results">
          {results.map((r) => (
            <button key={`${r.lat},${r.lon}`} onClick={() => void pick(r)} disabled={busy}>
              <b>{r.name}</b> <span>{r.region}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface SunJson {
  place: string | null;
  tz: string | null;
  date: string;
  today: { sunrise: number | null; sunset: number | null; noon: number; dawn: number | null; dusk: number | null; goldenMorningEnd: number | null; goldenEveningStart: number | null; daylight: number; polar: "day" | "night" | null };
  tomorrow: { sunrise: number | null; sunset: number | null; daylight: number };
  change: number;
  moon: { phase: number; lit: number; name: string; nextFull: number; nextNew: number };
}

/** The moon as it looks tonight: lit on the right as it grows, on the left as it shrinks (as seen from the north). */
function MoonDisc({ phase, size = 28 }: { phase: number; size?: number }) {
  const r = 10;
  const waxing = phase < 0.5;
  // how far the line between light and dark sits from the middle: 1 at new, -1 at full
  const k = Number.isFinite(phase) ? Math.cos(2 * Math.PI * phase) : 1;
  const rx = Math.abs(k) * r;
  const lit = `M0,${-r} A${r},${r} 0 0 ${waxing ? 1 : 0} 0,${r} A${rx},${r} 0 0 ${waxing ? (k > 0 ? 0 : 1) : k > 0 ? 1 : 0} 0,${-r} Z`;
  return (
    <svg className="sky-moon-disc" width={size} height={size} viewBox="-12 -12 24 24" aria-hidden>
      <circle r={r} className="sky-moon-dark" />
      <path d={lit} className="sky-moon-lit" />
    </svg>
  );
}

const hm = (min: number) => `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}m`;

/** Sunrise and sunset on the day's arc, with the sun where it is now, the daylight and how it changes, and the moon tonight. */
export function SunMoon({ item, stream, readOnly }: WP) {
  const [ref, size] = useSize<HTMLDivElement>();
  // redrawn each minute, so the sun moves along its arc
  useTick(60_000);
  const now = Date.now();
  const data = stream?.json as SunJson | undefined;
  if (!data?.today) return <PlaceSetup item={item} readOnly={readOnly} error={stream?.error ?? null} preset="sun" />;
  const tz = data.tz ?? undefined;
  const at = (ms: number | null) => {
    if (ms === null) return "—";
    try {
      return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", timeZone: tz }).format(ms);
    } catch {
      return new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }
  };
  const on = (ms: number) => {
    try {
      return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", timeZone: tz }).format(ms);
    } catch {
      return new Date(ms).toDateString();
    }
  };
  const t = data.today;
  // the sun's place on its arc, from sunrise (0) to sunset (1), while it is up
  const up = t.sunrise !== null && t.sunset !== null && t.sunset > t.sunrise && now >= t.sunrise && now <= t.sunset;
  const raw = up ? (now - t.sunrise!) / (t.sunset! - t.sunrise!) : null;
  // never a sun drawn at a number that is not one
  const p = raw !== null && Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : null;
  const sx = p === null ? 0 : 100 - 88 * Math.cos(Math.PI * p);
  const sy = p === null ? 0 : 96 - 82 * Math.sin(Math.PI * p);
  // the arc wants some height; the extras (how the day changes, the next full moon) some width
  const arc = size.w === 0 || (size.h >= 118 && size.w >= 190);
  const compact = size.w > 0 && size.w < 236;
  const day = t.polar === "day" ? "Midnight sun" : t.polar === "night" ? "Polar night" : `${hm(t.daylight)} of daylight`;
  const change = t.polar || !data.change ? "" : `${Math.abs(data.change)} min ${data.change > 0 ? "longer" : "shorter"} than yesterday`;
  const nextMoon = data.moon.nextFull < data.moon.nextNew ? `Full ${on(data.moon.nextFull)}` : `New ${on(data.moon.nextNew)}`;
  return (
    <div
      className={cx("sky-w", up || t.polar === "day" ? "is-day" : "is-night", compact && "is-compact")}
      ref={ref}
      title={`${data.place ?? item.title}: sunrise ${at(t.sunrise)}, sunset ${at(t.sunset)}. ${day}. ${data.moon.name}, ${Math.round(data.moon.lit * 100)}% lit.`}
    >
      {arc && (
        <svg className="sky-arc" viewBox="0 0 200 104" preserveAspectRatio="xMidYMax meet" aria-hidden>
          <path d="M12,96 A88,82 0 0 1 188,96" className="sky-arc-path" />
          {p !== null && <path d={`M12,96 A88,82 0 0 1 ${sx.toFixed(1)},${sy.toFixed(1)}`} className="sky-arc-done" />}
          <line x1="2" y1="96" x2="198" y2="96" className="sky-horizon" />
          {p !== null && <circle cx={sx} cy={sy} r="7" className="sky-sun" />}
        </svg>
      )}
      <div className="sky-times">
        <div>
          <span>Sunrise</span>
          <b>{at(t.sunrise)}</b>
        </div>
        <div className="sky-mid">
          <span>{day}</span>
          {change && !compact && <i>{change}</i>}
        </div>
        <div>
          <span>Sunset</span>
          <b>{at(t.sunset)}</b>
        </div>
      </div>
      <div className="sky-moon">
        <MoonDisc phase={data.moon.phase} size={compact ? 20 : 26} />
        <span>
          <b>{data.moon.name}</b> {Math.round(data.moon.lit * 100)}% lit{compact ? "" : ` · ${nextMoon}`}
        </span>
      </div>
    </div>
  );
}
